// ═══════════════════════════════════════════════════════════════════════════
// COLLAB-LOCK-COMMAND — transient per-dispatch veto OUTCOME signal
// ═══════════════════════════════════════════════════════════════════════════
//
// Not the veto DECISION itself (that is `evaluateCollabLockVeto` in the
// sibling `collabLockVeto.ts`, kept deliberately pure/dependency-free). This
// is the narrow, mutable, module-scoped "was the dispatch I just made
// rejected by the fail-closed collab-lock EDIT veto?" flag `editorStore.ts`'s
// `dispatch()` sets synchronously whenever `evaluateCollabLockVeto` returns
// non-null, and that ANY caller — inside `editorStore.ts` itself (the
// `history/undo`, `history/redo`, `history/replay-forwards`,
// `history/replay-inverses` handlers, which recursively re-`dispatch()` an
// inverse/forward with `__bypassHistory: true` and have no OTHER way to know
// whether that recursive dispatch actually landed) or from a DIFFERENT file
// (`editorStoreStyleTables.ts`'s `deleteLayer()`/`renameLayer()`, which
// dispatch `header/delete-layer`/`header/rename-layer` and need to know
// whether to report their own `{ ok: false, error }` instead of blindly
// `{ ok: true }`) — reads immediately after dispatching.
//
// GitHub Codex review (PR #534, round 2) P2 — extracted to its OWN leaf
// module rather than living as a private `let` inside `editorStore.ts` (as
// it originally did) specifically so `editorStoreStyleTables.ts` can read it
// too: `editorStore.ts` already imports FROM `editorStoreStyleTables.ts`
// (the `StyleTableActionsSlice`), so a DIRECT import the other way
// (`editorStoreStyleTables.ts` importing straight from `editorStore.ts`)
// would create a two-file dependency cycle — ПРИОРИТЕТ #2's hard zero-cycle
// gate. This standalone module has zero imports of its own, so BOTH files
// can depend on it without depending on each other, mirroring the existing
// `collabLockVeto.ts` leaf-module precedent for the same reason.
//
// Reset to `false` as the FIRST statement of every `dispatch()` call
// (including recursive/nested ones, via `resetDispatchCollabLockVetoFlag()`)
// so a caller that dispatches, then immediately reads the flag, sees the
// outcome of THAT call only — never a stale value left over from an
// unrelated earlier dispatch. A history/replay handler that fans out over
// multiple sub-dispatches (a BATCHED entry's `forwards`/`inverses` array, or
// `abort-batch`'s inverse loop) accumulates the per-iteration flag value and
// re-marks it before its OWN `return`, so a caller nested one level up (e.g.
// `undo()` dispatching a batched entry's `history/replay-inverses`) still
// observes the correct aggregate.
let lastDispatchWasCollabLockVetoed = false;

/** Clear the flag — called as the first statement of every `dispatch()` call. */
export function resetDispatchCollabLockVetoFlag(): void {
  lastDispatchWasCollabLockVetoed = false;
}

/** Mark that the dispatch just made (or just aggregated over a batch) was vetoed. */
export function markDispatchCollabLockVetoed(): void {
  lastDispatchWasCollabLockVetoed = true;
}

/** Read-only accessor — see the module header for the reset-per-call contract. */
export function wasLastDispatchCollabLockVetoed(): boolean {
  return lastDispatchWasCollabLockVetoed;
}
